import React, { useCallback } from 'react';
import { MdDarkMode, MdLightMode } from 'react-icons/md';
import { IconButton, TopBar } from '@aibuild/ui';
import FeedbackWidget from '@components/FeedbackWidget/FeedbackWidget';
import UserProfile from '@components/UserProfile';
import MobileMenuButton from '@components/MobileMenuButton';
import { useTheme } from '@contexts/useTheme';
import ProcessEditorBreadcrumb from './ProcessEditorBreadcrumb';
import SaveStatusIndicator from './SaveStatusIndicator';
import type { AutoSaveStatus } from '../useProcessAutoSave';

export interface ProcessEditorTopBarProps {
  processName: string;
  saveStatus?: AutoSaveStatus;
  saveLastSavedAt?: string | null;
  saveErrorMessage?: string | null;
  saveValidationErrorCount?: number;
  onSaveRefresh?: () => void;
  /** A conditional GET for external changes is in flight. */
  isSyncing?: boolean;
  /** Server changes we could not apply because the canvas is dirty. */
  changesWaiting?: boolean;
  onBackToList: () => void;
  onCommitRename?: (newName: string) => void;
  dataTestId?: string;
}

/**
 * Thin shell topbar — breadcrumb + save status + theme toggle + profile.
 * Actions (publish, run, etc.) now live in the ProcessSidePanel dock.
 */
const ProcessEditorTopBar: React.FC<ProcessEditorTopBarProps> = ({
  processName,
  saveStatus,
  saveLastSavedAt = null,
  saveErrorMessage = null,
  saveValidationErrorCount = 0,
  onSaveRefresh,
  isSyncing = false,
  changesWaiting = false,
  onBackToList,
  onCommitRename,
  dataTestId = 'process-editor-topbar',
}) => {
  const { themeName, setThemePreference } = useTheme();
  const isDark = themeName === 'dark';
  const onToggleTheme = useCallback(() => {
    setThemePreference(isDark ? 'light' : 'dark');
  }, [isDark, setThemePreference]);

  return (
    <TopBar
      dataTestId={dataTestId}
      leading={<MobileMenuButton />}
      leftSlot={
        <ProcessEditorBreadcrumb
          processName={processName}
          onBackToList={onBackToList}
          onCommitRename={onCommitRename}
          dataTestId={`${dataTestId}__breadcrumb`}
        />
      }
      actions={
        <>
          {saveStatus ? (
            <SaveStatusIndicator
              status={saveStatus}
              lastSavedAt={saveLastSavedAt}
              errorMessage={saveErrorMessage}
              validationErrorCount={saveValidationErrorCount}
              onRefresh={onSaveRefresh}
              isSyncing={isSyncing}
              changesWaiting={changesWaiting}
              dataTestId={`${dataTestId}__save-status`}
            />
          ) : null}
          <FeedbackWidget mode="inline" dataTestId={`${dataTestId}__feedback`} />
          <IconButton
            ariaLabel={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
            icon={isDark ? <MdLightMode size={20} /> : <MdDarkMode size={20} />}
            onClick={onToggleTheme}
            dataTestId={`${dataTestId}__theme-toggle`}
          />
          <UserProfile />
        </>
      }
    />
  );
};

export default ProcessEditorTopBar;
